import React, { useContext, useState } from 'react';
import { Box, Popover, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import HelpContext from '../../contexts/HelpContext';
import { useHelpBundle } from './helpBundle';
import { usePlanLimitContent, PLAN_LIMIT_PREFIX } from './planLimitContent';
import { radius, elevation, helpDark } from '../../themes/smartinventoryTheme';
import eventBus from '../../utils/eventBus';

// "**bold**" segments render in helpDark.text (the popover's brightest tone)
// inside body copy that's otherwise helpDark.body — matches the mockup's
// "Which orders go on this invoice — Every **paid** order..." example.
const renderBoldSegments = (text) => {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const match = /^\*\*([^*]+)\*\*$/.exec(part);
    if (!match) return <React.Fragment key={i}>{part}</React.Fragment>;
    return <Box key={i} component="strong" sx={{ color: helpDark.text }}>{match[1]}</Box>;
  });
};

// `<HelpMark id="invoices.period" variant="outlined" />` — a 15px `?` that
// opens a dark popover explaining a rule the UI doesn't otherwise state.
// `id` is either a plain content id (looked up in help.json `marks.<id>`) or
// `plan-limit.<featureKey>` (the shared live template — see planLimitContent.js).
// "Read more" opens the full help panel to the matching article.
//
// Reads HelpContext directly (not the throwing `useHelp()` hook) because
// HelpMark now renders inside shared components (UsageMeter, invoice/user
// dialogs) that plenty of existing unit tests mount stand-alone, without a
// HelpProvider — the popover still shows its content there; only "Read
// more" becomes a no-op, which never comes up outside the real app shell.
const HelpMark = ({ id, variant = 'outlined', plans }) => {
  const { t } = useTranslation('help');
  const bundle = useHelpBundle();
  const help = useContext(HelpContext);
  const [anchorEl, setAnchorEl] = useState(null);

  const isPlanLimit = id && id.startsWith(PLAN_LIMIT_PREFIX);
  const featureKey = isPlanLimit ? id.slice(PLAN_LIMIT_PREFIX.length) : null;
  const planLimitContent = usePlanLimitContent(featureKey, plans);
  const mark = !isPlanLimit ? bundle.marks?.[id] : null;

  const title = isPlanLimit ? planLimitContent?.popoverTitle : mark?.title;
  const body = isPlanLimit ? planLimitContent?.popoverBody : mark?.body;
  if (!title && !body) return null;

  const open = Boolean(anchorEl);
  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  // A mark's own id doubles as its article id for plan-limit marks; a
  // regular content mark may instead point `articleId` at whichever full
  // article already covers the same rule (see help.json `marks`), or omit
  // it — "Read more" then falls back to this page's topic list.
  const readMoreTarget = isPlanLimit ? id : (mark?.articleId || null);
  const handleReadMore = () => {
    if (!help) return;
    eventBus.emit(eventBus.EVENTS.HELP_TOPIC_OPEN, { articleId: readMoreTarget || id, viaMark: true });
    handleClose();
    help.open(readMoreTarget || undefined);
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label={title || t('panel.title', { defaultValue: 'Help' })}
        data-testid={`help-mark-${id}`}
        onClick={handleOpen}
        sx={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 15, height: 15, borderRadius: '50%', ml: 0.5, p: 0, cursor: 'pointer',
          fontSize: 9.5, fontWeight: 700, lineHeight: 1, flexShrink: 0,
          border: variant === 'filled' ? 'none' : '1.5px solid',
          borderColor: variant === 'filled' ? 'transparent' : 'primary.light',
          bgcolor: variant === 'filled' ? 'primary.main' : 'transparent',
          color: variant === 'filled' ? 'primary.contrastText' : 'primary.main',
        }}
      >
        ?
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5, maxWidth: 280, p: '12px 14px', borderRadius: `${radius.lg}px`,
              bgcolor: helpDark.bg, color: helpDark.text, boxShadow: elevation.helpPopover,
              position: 'relative', overflow: 'visible',
              '&::before': {
                content: '""', position: 'absolute', top: -6, left: 16,
                width: 12, height: 12, bgcolor: helpDark.bg, transform: 'rotate(45deg)',
              },
            },
          },
        }}
      >
        {title && <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5, color: helpDark.text }}>{title}</Typography>}
        {body && <Typography sx={{ fontSize: 12.5, color: helpDark.body, lineHeight: 1.5 }}>{renderBoldSegments(body)}</Typography>}
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Box
            component="button"
            type="button"
            onClick={handleReadMore}
            sx={{
              background: 'none', border: 'none', p: 0, cursor: 'pointer',
              fontSize: 12, fontWeight: 600, color: helpDark.link,
            }}
          >
            {t('planLimit.readMore', { defaultValue: 'Read more' })}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default HelpMark;
